import { createFeature, createReducer, on } from '@ngrx/store';
import { Quote } from '@zero/shared/util-models';
import { AssetWithPerformance } from '../entities/asset.entity';
import { CallMoneyAlternatives } from '../entities/call-money-alternatives.entity';
import { EtfsByIndices } from '../entities/etfs-by-indices.entity';
import { InstrumentMostPopular } from '../entities/instrument-most-popular.entity';
import { MostActiveAssets } from '../entities/most-active-assets.entity';
import { MostPopular } from '../entities/most-popular.entity';
import { WinnerLoser } from '../entities/winner-loser.entity';
import { assetClassOverviewActions, assetClassOverviewApiActions } from './asset-class-overview.actions';

export interface AssetClassOverviewState {
  loadingEtfsByIndices: boolean;
  etfsByIndices: EtfsByIndices[];
  loadingMostPopularEtfs: boolean;
  mostPopularEtfs: MostPopular;
  loadingMostPopularStocks: boolean;
  mostPopularStocks: MostPopular;
  loadingCallMoneyAlternatives: boolean;
  callMoneyAlternatives: CallMoneyAlternatives[];
  loadingMostActiveAssets: boolean;
  mostActiveAssets: MostActiveAssets;
  loadingWinnerLoser: boolean;
  winnerLoser: WinnerLoser;
}

const initialState: AssetClassOverviewState = {
  loadingEtfsByIndices: null,
  etfsByIndices: null,
  loadingMostPopularEtfs: null,
  mostPopularEtfs: null,
  loadingMostPopularStocks: null,
  mostPopularStocks: null,
  loadingCallMoneyAlternatives: null,
  callMoneyAlternatives: null,
  loadingMostActiveAssets: null,
  mostActiveAssets: null,
  loadingWinnerLoser: null,
  winnerLoser: null,
};

const getPerfPct = (curr: number, prev: number) => (prev ? ((curr - prev) / prev) * 100 : 0);
const getPerformance = (asset: AssetWithPerformance | InstrumentMostPopular, quotes: Record<string, Quote>) => {
  let historicQuoteAsk: number | undefined;
  if ('historicQuoteAsk' in asset) {
    historicQuoteAsk = asset.historicQuoteAsk;
  } else if ('historicQuote' in asset || 'historicQuotes' in asset) {
    historicQuoteAsk = asset.historicQuote?.ask ?? asset.historicQuotes?.[0]?.ask;
  }
  return historicQuoteAsk && quotes[asset.isin] ? getPerfPct(quotes[asset.isin].ask, historicQuoteAsk) : asset.performance;
};

export const assetClassOverviewFeature = createFeature({
  name: 'assetClassOverview',
  reducer: createReducer(
    initialState,
    on(
      assetClassOverviewActions.loadEtfsByIndices,
      (state): AssetClassOverviewState => ({
        ...state,
        loadingEtfsByIndices: true,
      }),
    ),
    on(
      assetClassOverviewApiActions.loadEtfsByIndicesSuccess,
      (state, { response }): AssetClassOverviewState => ({
        ...state,
        loadingEtfsByIndices: false,
        etfsByIndices: response,
      }),
    ),
    on(
      assetClassOverviewApiActions.loadEtfsByIndicesFailure,
      (state): AssetClassOverviewState => ({
        ...state,
        loadingEtfsByIndices: false,
      }),
    ),
    on(
      assetClassOverviewActions.loadMostPopularEtfs,
      (state): AssetClassOverviewState => ({
        ...state,
        loadingMostPopularEtfs: true,
      }),
    ),
    on(
      assetClassOverviewApiActions.loadMostPopularEtfsSuccess,
      (state, { response }): AssetClassOverviewState => ({
        ...state,
        loadingMostPopularEtfs: false,
        mostPopularEtfs: response,
      }),
    ),
    on(
      assetClassOverviewApiActions.loadMostPopularEtfsFailure,
      (state): AssetClassOverviewState => ({
        ...state,
        loadingMostPopularEtfs: false,
      }),
    ),
    on(
      assetClassOverviewActions.loadMostPopularStocks,
      (state): AssetClassOverviewState => ({
        ...state,
        loadingMostPopularStocks: true,
      }),
    ),
    on(
      assetClassOverviewApiActions.loadMostPopularStocksSuccess,
      (state, { response }): AssetClassOverviewState => ({
        ...state,
        loadingMostPopularStocks: false,
        mostPopularStocks: response,
      }),
    ),
    on(
      assetClassOverviewApiActions.loadMostPopularStocksFailure,
      (state): AssetClassOverviewState => ({
        ...state,
        loadingMostPopularStocks: false,
      }),
    ),
    on(
      assetClassOverviewActions.loadMostActiveAssets,
      (state): AssetClassOverviewState => ({
        ...state,
        loadingMostActiveAssets: true,
      }),
    ),
    on(
      assetClassOverviewApiActions.loadMostActiveAssetsSuccess,
      (state, { response }): AssetClassOverviewState => ({
        ...state,
        loadingMostActiveAssets: false,
        mostActiveAssets: response,
      }),
    ),
    on(
      assetClassOverviewApiActions.loadMostActiveAssetsFailure,
      (state): AssetClassOverviewState => ({
        ...state,
        loadingMostActiveAssets: false,
      }),
    ),
    on(
      assetClassOverviewActions.loadCallMoneyAlternatives,
      (state): AssetClassOverviewState => ({
        ...state,
        loadingCallMoneyAlternatives: true,
      }),
    ),
    on(
      assetClassOverviewApiActions.loadCallMoneyAlternativesSuccess,
      (state, { response }): AssetClassOverviewState => ({
        ...state,
        loadingCallMoneyAlternatives: false,
        callMoneyAlternatives: response,
      }),
    ),
    on(
      assetClassOverviewApiActions.loadCallMoneyAlternativesFailure,
      (state): AssetClassOverviewState => ({
        ...state,
        loadingCallMoneyAlternatives: false,
      }),
    ),
    on(
      assetClassOverviewActions.loadWinnerLoser,
      (state): AssetClassOverviewState => ({
        ...state,
        loadingWinnerLoser: true,
      }),
    ),
    on(
      assetClassOverviewApiActions.loadWinnerLoserSuccess,
      (state, { response }): AssetClassOverviewState => ({
        ...state,
        loadingWinnerLoser: false,
        winnerLoser: response,
      }),
    ),
    on(
      assetClassOverviewApiActions.loadWinnerLoserFailure,
      (state): AssetClassOverviewState => ({
        ...state,
        loadingWinnerLoser: false,
      }),
    ),
    on(assetClassOverviewActions.updateMostPopularEtfsQuotes, (state, { quotes }): AssetClassOverviewState => {
      return {
        ...state,
        mostPopularEtfs: {
          ...state.mostPopularEtfs,
          position: state.mostPopularEtfs.position.map((etf) => ({
            ...etf,
            quote: quotes[etf.isin] ?? etf.quote,
            performance: getPerformance(etf, quotes),
          })),
          watchlist: state.mostPopularEtfs.watchlist.map((etf) => ({
            ...etf,
            quote: quotes[etf.isin] ?? etf.quote,
            performance: getPerformance(etf, quotes),
          })),
          savingsPlan: state.mostPopularEtfs.savingsPlan.map((etf) => ({
            ...etf,
            quote: quotes[etf.isin] ?? etf.quote,
            performance: getPerformance(etf, quotes),
          })),
        },
      };
    }),
    on(assetClassOverviewActions.updateMostPopularStocksQuotes, (state, { quotes }): AssetClassOverviewState => {
      return {
        ...state,
        mostPopularStocks: {
          ...state.mostPopularStocks,
          position: state.mostPopularStocks.position.map((stock) => ({
            ...stock,
            quote: quotes[stock.isin] ?? stock.quote,
            performance: getPerformance(stock, quotes),
          })),
          watchlist: state.mostPopularStocks.watchlist.map((stock) => ({
            ...stock,
            quote: quotes[stock.isin] ?? stock.quote,
            performance: getPerformance(stock, quotes),
          })),
          savingsPlan: state.mostPopularStocks.savingsPlan.map((stock) => ({
            ...stock,
            quote: quotes[stock.isin] ?? stock.quote,
            performance: getPerformance(stock, quotes),
          })),
        },
      };
    }),
    on(assetClassOverviewActions.updateMostActiveAssetsQuotes, (state, { quotes }): AssetClassOverviewState => {
      return {
        ...state,
        mostActiveAssets: {
          ...state.mostActiveAssets,
          instrumentsByBuyRank: state.mostActiveAssets.instrumentsByBuyRank.map((etf) => ({
            ...etf,
            quote: quotes[etf.isin] ?? etf.quote,
            performance: getPerformance(etf, quotes),
          })),
          instrumentsBySellRank: state.mostActiveAssets.instrumentsBySellRank.map((etf) => ({
            ...etf,
            quote: quotes[etf.isin] ?? etf.quote,
            performance: getPerformance(etf, quotes),
          })),
          instrumentsByRank: state.mostActiveAssets.instrumentsByRank.map((etf) => ({
            ...etf,
            quote: quotes[etf.isin] ?? etf.quote,
            performance: getPerformance(etf, quotes),
          })),
        },
      };
    }),
  ),
});
